/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import type { ComponentProps } from 'react';
import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import { MinusIcon, PlusIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function Accordion({ ...props }: ComponentProps<typeof Root>) {
    return <Root data-slot="accordion" {...props} />;
}

function AccordionItem({ className, ...props }: ComponentProps<typeof Item>) {
    return (
        <Item data-slot="accordion-item" className={cn('border-b border-border last:border-b', className)} {...props} />
    );
}

function AccordionTrigger({ className, children, ...props }: ComponentProps<typeof Trigger>) {
    return (
        <Header className="flex w-full">
            <Trigger
                data-slot="accordion-trigger"
                className={cn(
                    'group flex w-full cursor-pointer items-center justify-between gap-4 py-4 text-left text-sm leading-5 font-semibold tracking-button text-foreground uppercase outline-none transition-colors disabled:pointer-events-none disabled:opacity-50',
                    'focus-visible:ring-0 focus-visible:outline-none',
                    className
                )}
                {...props}>
                <span
                    data-slot="accordion-trigger-label"
                    className="min-w-0 flex-1 truncate [text-decoration-skip-ink:none] decoration-accent-secondary decoration-thin decoration-solid group-hover:underline group-focus-visible:underline">
                    {children}
                </span>
                <PlusIcon
                    aria-hidden="true"
                    className="size-4 shrink-0 group-data-[state=open]:hidden"
                    strokeWidth={1.5}
                />
                <MinusIcon
                    aria-hidden="true"
                    className="hidden size-4 shrink-0 group-data-[state=open]:block"
                    strokeWidth={1.5}
                />
            </Trigger>
        </Header>
    );
}

function AccordionContent({ className, children, ...props }: ComponentProps<typeof Content>) {
    return (
        <Content
            data-slot="accordion-content"
            className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm leading-5 text-foreground"
            {...props}>
            <div className={cn('pt-0 pb-4', className)}>{children}</div>
        </Content>
    );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
